import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, ArrowRight, TiktokLogo, YoutubeLogo, TwitchLogo,
  InstagramLogo, XLogo, IdentificationCard, Prohibit, CheckCircle, Trash, MagnifyingGlass, Users, GameController, DiscordLogo, SortAscending,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { OWNER } from "@/constants/testIds";

const PLATFORMS = [
  { value: "tiktok", label: "TikTok", Icon: TiktokLogo },
  { value: "youtube", label: "YouTube", Icon: YoutubeLogo },
  { value: "twitch", label: "Twitch", Icon: TwitchLogo },
  { value: "instagram", label: "Instagram", Icon: InstagramLogo },
  { value: "x", label: "X / Twitter", Icon: XLogo },
  { value: "other", label: "Other", Icon: IdentificationCard },
];

const emptyForm = {
  slug: "", display_name: "", email: "", password: "", tebex_creator_code: "",
  commission_pct: 10, attribution_window_days: 30, discord_invite_url: "",
};

function Field({ label, hint, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="kbd-label mb-1">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-white/40 mt-1 font-mono">{hint}</div>}
    </div>
  );
}

function Input(props) {
  return <input {...props} className={`w-full bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm focus:border-neon-cyan outline-none ${props.className || ""}`} />;
}

function Section({ title, icon: Icon, iconColor = "#00F0FF", children }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 border-b border-white/10 pb-2">
        {Icon && <Icon size={18} color={iconColor} weight="fill" />}
        <h3 className="display-heading font-bold text-sm">{title}</h3>
      </div>
      <div className="grid md:grid-cols-2 gap-3">{children}</div>
    </section>
  );
}

export default function CreatorsTab() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("newest");

  async function load() {
    const r = await api.get("/creators");
    setRows(r.data);
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = rows;
    if (q) {
      list = rows.filter((c) => {
        const hay = [
          c.display_name, c.slug, c.email, c.contact_email, c.primary_platform,
          c.payout_method, c.status, c.handle_tiktok, c.handle_youtube, c.handle_twitch,
          c.handle_instagram, c.handle_x, c.promo_code,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      });
    }
    const cmp = {
      az: (a, b) => (a.display_name || "").localeCompare(b.display_name || ""),
      za: (a, b) => (b.display_name || "").localeCompare(a.display_name || ""),
      newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      oldest: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      revenue: (a, b) => (b.revenue_total || 0) - (a.revenue_total || 0),
      members: (a, b) => (b.member_count || 0) - (a.member_count || 0),
    }[sortKey] || (() => 0);
    return [...list].sort(cmp);
  }, [rows, query, sortKey]);

  async function create(e) {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.password) delete payload.password;
      if (!payload.tebex_creator_code) delete payload.tebex_creator_code;
      if (!payload.discord_invite_url) delete payload.discord_invite_url;
      const res = await api.post("/creators", payload);
      toast.success(`Creator ${form.slug} created`);
      if (form.discord_invite_url) {
        navigator.clipboard.writeText(form.discord_invite_url).catch(() => {});
        toast.info("Discord invite copied to clipboard");
      }
      setShowCreate(false);
      setForm(emptyForm);
      nav(`/owner/creators/${res.data.creator.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  }

  function completeness(c) {
    const wanted = ["avatar_url", "primary_platform", "payout_method", "payout_address", "contact_email"];
    const filled = wanted.filter((k) => c[k]).length;
    return Math.round((filled / wanted.length) * 100);
  }

  async function toggleStatus(c) {
    const next = c.status === "active" ? "disabled" : "active";
    try {
      await api.patch(`/creators/${c.id}`, { status: next });
      toast.success(`${c.display_name} ${next === "active" ? "activated" : "deactivated"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    }
  }

  async function remove(c) {
    if (!window.confirm(`Permanently delete creator "${c.display_name}"? Their Tebex sales are kept but unlinked. This cannot be undone.`)) return;
    try {
      await api.delete(`/creators/${c.id}`);
      toast.success(`Creator ${c.slug} deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="display-heading font-black text-3xl">Creators</h2>
          <div className="kbd-label text-white/40 mt-1">ROSTER · PROFILES · PAYOUT · COMMISSIONS</div>
        </div>
        <button onClick={() => setShowCreate(!showCreate)} data-testid={OWNER.createCreatorBtn} className="neon-btn px-4 py-2 inline-flex items-center gap-2 kbd-label">
          <Plus size={14} weight="bold" /> NEW CREATOR
        </button>
      </div>

      {/* Quick-create form: just essentials */}
      {showCreate && (
        <form onSubmit={create} className="card-tactical p-6 space-y-3">
          <div className="grid md:grid-cols-4 gap-3">
            <Input required placeholder="slug" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid={OWNER.createCreatorSlug} />
            <Input required placeholder="Display name" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid={OWNER.createCreatorName} />
            <Input type="email" placeholder="login email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={OWNER.createCreatorEmail} />
            <Input type="password" placeholder="login password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="create-creator-password" />
          </div>
          <div className="grid md:grid-cols-4 gap-3">
            <Input placeholder="Tebex code (optional)" value={form.tebex_creator_code} onChange={(e) => setForm({ ...form, tebex_creator_code: e.target.value })} data-testid={OWNER.createCreatorTebexCode} />
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <div className="kbd-label mb-1 text-neon-yellow">COMMISSION %</div>
              <Input type="number" step="0.1" placeholder="10" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: parseFloat(e.target.value) || 0 })} data-testid={OWNER.createCreatorCommission} />
            </div>
            <div>
              <div className="kbd-label mb-1 text-neon-cyan">WINDOW · DAYS (0 = ∞)</div>
              <Input type="number" step="1" min="0" placeholder="30" value={form.attribution_window_days} onChange={(e) => setForm({ ...form, attribution_window_days: parseInt(e.target.value, 10) || 0 })} data-testid="create-creator-window" />
            </div>
            <div>
              <div className="kbd-label mb-1">DISCORD INVITE URL</div>
              <Input placeholder="https://discord.gg/…" value={form.discord_invite_url} onChange={(e) => setForm({ ...form, discord_invite_url: e.target.value })} data-testid="create-creator-invite" />
            </div>
          </div>
          <p className="text-xs text-white/50 font-mono">
            Fill the essentials → click <span className="text-neon-cyan">CREATE</span>. Add profile / payout / social details in the <span className="text-neon-yellow">MANAGE</span> drawer after.
          </p>
          <button type="submit" data-testid={OWNER.createCreatorSubmit} className="neon-btn px-4 py-2 kbd-label w-full">CREATE CREATOR</button>
        </form>
      )}

      {/* Search + sort */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[300px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search creators by name, slug, email, platform, promo code…"
            data-testid="creators-search-input"
            className="w-full bg-black/60 border border-white/10 pl-10 pr-4 py-2.5 font-mono text-sm focus:border-neon-cyan outline-none"
          />
        </div>
        <div className="relative">
          <SortAscending size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            data-testid="creators-sort-select"
            className="bg-black/60 border border-white/10 pl-9 pr-4 py-2.5 font-mono text-sm focus:border-neon-cyan outline-none appearance-none cursor-pointer"
          >
            <option value="newest">SORT · NEWEST FIRST</option>
            <option value="oldest">SORT · OLDEST FIRST</option>
            <option value="az">SORT · A → Z</option>
            <option value="za">SORT · Z → A</option>
            <option value="revenue">SORT · MOST REVENUE</option>
            <option value="members">SORT · MOST MEMBERS</option>
          </select>
        </div>
      </div>
      {query && (
        <div className="text-[10px] text-white/40 font-mono -mt-3">
          {filtered.length} of {rows.length} shown
        </div>
      )}

      {/* Roster table */}
      <div className="card-tactical overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="kbd-label text-left text-white/50 border-b border-white/10">
              <th className="px-6 py-3">CREATOR</th>
              <th>PLATFORM</th>
              <th>DISCORD</th>
              <th>FIVEM · PLAYING NOW</th>
              <th>REVENUE</th>
              <th>COMMISSION</th>
              <th>WINDOW</th>
              <th>STATUS</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {filtered.map((c) => {
              const pct = completeness(c);
              const plat = PLATFORMS.find((p) => p.value === c.primary_platform);
              const PlatIcon = plat?.Icon;
              return (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={OWNER.creatorRow(c.slug)}>
                  <td className="px-6 py-3">
                    <button
                      onClick={() => nav(`/owner/creators/${c.id}`)}
                      data-testid={`open-creator-${c.slug}`}
                      className="flex items-center gap-3 text-left group"
                    >
                      {c.avatar_url ? (
                        <img src={c.avatar_url} alt="" className="w-8 h-8 rounded-none border border-white/10 object-cover group-hover:border-neon-cyan transition-colors" />
                      ) : (
                        <div className="w-8 h-8 bg-white/5 border border-white/10 flex items-center justify-center text-xs text-white/40 font-sans group-hover:border-neon-cyan transition-colors">
                          {c.display_name?.[0]?.toUpperCase() || "?"}
                        </div>
                      )}
                      <div>
                        <div className="font-sans group-hover:text-neon-cyan transition-colors">{c.display_name}</div>
                        <div className="text-xs text-white/40">@{c.slug}</div>
                      </div>
                    </button>
                  </td>
                  <td>
                    {plat ? (
                      <span className="inline-flex items-center gap-1 text-white">
                        <PlatIcon size={13} weight="fill" color="#00F0FF" /> {plat.label}
                      </span>
                    ) : <span className="text-white/30">—</span>}
                  </td>
                  <td>
                    <div className="inline-flex items-center gap-1.5 text-white/80" data-testid={`creator-discord-${c.slug}`}>
                      <DiscordLogo size={13} weight="fill" color="#5865F2" />
                      <span className="text-neon-cyan">{c.member_count ?? 0}</span>
                    </div>
                  </td>
                  <td>
                    {c.playing_now > 0 ? (
                      <div className="flex flex-col gap-0.5" data-testid={`creator-playing-${c.slug}`}>
                        <div className="inline-flex items-center gap-1 text-emerald-400">
                          <GameController size={12} weight="fill" />
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                          <span>{c.playing_now} LIVE</span>
                        </div>
                        {c.playing_now_names && c.playing_now_names.length > 0 && (
                          <div className="text-[10px] text-white/60 font-sans truncate max-w-[180px]" title={c.playing_now_names.join(", ")}>
                            {c.playing_now_names.slice(0, 2).join(", ")}
                            {c.playing_now_names.length > 2 && ` +${c.playing_now_names.length - 2}`}
                          </div>
                        )}
                      </div>
                    ) : <span className="text-white/30">—</span>}
                  </td>
                  <td className="text-emerald-400 font-mono">
                    {c.revenue_total > 0 ? `$${c.revenue_total.toFixed(2)}` : <span className="text-white/30">—</span>}
                  </td>
                  <td className="text-neon-yellow">{c.commission_pct}%</td>
                  <td className="text-neon-cyan">{(c.attribution_window_days ?? 30) === 0 ? "∞" : `${c.attribution_window_days ?? 30}d`}</td>
                  <td>
                    <span className={c.status === "active" ? "text-emerald-400" : "text-red-400"}>{c.status}</span>
                  </td>
                  <td className="px-3">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => nav(`/owner/creators/${c.id}`)}
                        data-testid={`manage-creator-${c.slug}`}
                        className="ghost-btn px-3 py-1 text-xs text-white inline-flex items-center gap-1"
                      >
                        OPEN <ArrowRight size={12} />
                      </button>
                      <button
                        onClick={() => toggleStatus(c)}
                        data-testid={`toggle-creator-${c.slug}`}
                        title={c.status === "active" ? "Deactivate" : "Activate"}
                        className={`px-3 py-1 text-xs inline-flex items-center gap-1 border ${c.status === "active" ? "text-white/70 border-white/20 hover:border-white/40" : "text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10"}`}
                      >
                        {c.status === "active" ? <><Prohibit size={12} /> DEACTIVATE</> : <><CheckCircle size={12} weight="fill" /> ACTIVATE</>}
                      </button>
                      <button
                        onClick={() => remove(c)}
                        data-testid={`delete-creator-${c.slug}`}
                        title="Delete permanently"
                        className="px-3 py-1 text-xs inline-flex items-center gap-1 text-red-400 border border-red-500/40 hover:bg-red-500/10"
                      >
                        <Trash size={12} /> DELETE
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={9} className="px-6 py-6 text-white/40">{query ? `No creators match "${query}".` : "No creators yet."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
